import AppSwitch from "@toothfairy/shared-ui/AppSwitch";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet, View, Image } from "react-native";
import QRCode from "qrcode";
import ToothFairyButton from "../../../Components/ToothFairyComponents/ToothFairyButton";
import Constants from "../../../Constants";

const TEAL = "#2A9D8F";
const CORAL = "#E76F51";
const AMBER = "#E9C46A";

const MobilePeeringSection = () => {
  const { currentTheme } = AppTheme.useTheme();
  const styles = styleGenerator(currentTheme);
  const AppToasty = AppToast.useToast();
  const { t } = useTranslation();
  const isElectron = !!window?.electronAPI;

  const [lan, setLan] = useState(null);
  const [lanPending, setLanPending] = useState(false);
  const [turnOnBusy, setTurnOnBusy] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  const fetchLan = () => {
    if (!isElectron) return;
    if (window.electronAPI?.getTfcodeStatus) {
      window.electronAPI
        .getTfcodeStatus()
        .then((s) => {
          const next = { ...(s?.lan || {}), running: !!s?.running };
          setLan((prev) =>
            JSON.stringify(prev) === JSON.stringify(next) ? prev : next
          );
        })
        .catch(() => {});
    } else if (window.electronAPI?.getLanConfig) {
      window.electronAPI
        .getLanConfig()
        .then((c) => setLan(c))
        .catch(() => {});
    }
  };

  useEffect(() => {
    fetchLan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lanReady = !!(lan?.enabled && lan?.port && lan?.password);
  const needsRestart = !!(lan?.enabled && lan?.running && !lan?.password);
  const lanWaiting = !!(lan?.enabled && !lanReady && !needsRestart);

  useEffect(() => {
    if (!isElectron || !lan?.enabled || lanReady || needsRestart)
      return undefined;
    const timer = setInterval(fetchLan, 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lan?.enabled, lanReady, needsRestart, isElectron]);

  const handleLanToggle = async (value) => {
    if (!window.electronAPI?.setLanConfig) {
      AppToasty.show(
        t(
          "profile.relayUnavailable",
          "You can only change this in the desktop app."
        ),
        { type: "danger" }
      );
      return;
    }
    setLanPending(true);
    try {
      const next = await window.electronAPI.setLanConfig(value);
      setLan(next);
    } catch (e) {
      AppToasty.show(
        `${t("profile.remoteToggleError", "Couldn't change this setting.")}${
          e?.message ? ` — ${e.message}` : ""
        }`,
        {
          type: "danger",
        }
      );
    } finally {
      setLanPending(false);
    }
  };

  const handleTurnOnNow = async () => {
    if (!window.electronAPI?.startTfcode) {
      AppToasty.show(
        t(
          "profile.relayUnavailable",
          "You can only change this in the desktop app."
        ),
        { type: "danger" }
      );
      return;
    }
    setTurnOnBusy(true);
    try {
      await window.electronAPI.startTfcode();
      fetchLan();
    } catch (e) {
      AppToasty.show(
        `${t("profile.remoteToggleError", "Couldn't change this setting.")}${
          e?.message ? ` — ${e.message}` : ""
        }`,
        { type: "danger" }
      );
    } finally {
      setTurnOnBusy(false);
    }
  };

  const copyLanValue = (text, key) => {
    window.electronAPI.clipboardWriteText(String(text)).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    });
  };

  const [relay, setRelay] = useState({ url: null, status: null, error: null });
  const [relayCode, setRelayCode] = useState(null);
  const [relayQrImage, setRelayQrImage] = useState(null);
  const [relayBusy, setRelayBusy] = useState(false);

  const refreshRelay = () => {
    if (!isElectron || !window.electronAPI?.relayGetUrl) return;
    window.electronAPI
      .relayGetUrl()
      .then((url) => setRelay((p) => ({ ...p, url })));
    if (window.electronAPI?.relayStatus) {
      window.electronAPI
        .relayStatus()
        .then((s) => setRelay((p) => ({ ...p, status: s })));
    }
  };

  useEffect(() => {
    refreshRelay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const relayState = relay?.status?.status;
  const relayDotColor =
    relayState === "online"
      ? TEAL
      : relayState === "connecting" ||
          relayState === "registering" ||
          relayState === "reconnecting"
        ? AMBER
        : "#888888";
  const relayStateLabel = relay?.error
    ? relay.error
    : relayState === "online"
      ? t(
          "profile.relayStatusOn",
          "On — your phone can reach this computer"
        )
      : relayState === "connecting" ||
          relayState === "registering" ||
          relayState === "reconnecting"
        ? t("profile.relayStatusConnecting", "Connecting…")
        : t("profile.relayStatusOff", "Off");

  const handleRelayStart = async () => {
    if (!window.electronAPI?.relayStart) {
      setRelay((p) => ({
        ...p,
        error: t(
          "profile.relayUnavailable",
          "You can only change this in the desktop app."
        ),
      }));
      return;
    }
    setRelayBusy(true);
    try {
      const s = await window.electronAPI.relayStart();
      setRelay((p) => ({
        ...p,
        status: s,
        error: s?.status === "error" ? s?.error ?? null : null,
      }));
    } catch (e) {
      setRelay((p) => ({ ...p, error: e?.message }));
    } finally {
      setRelayBusy(false);
    }
  };

  const handleRelayStop = async () => {
    if (!window.electronAPI?.relayStop) {
      setRelay((p) => ({
        ...p,
        error: t(
          "profile.relayUnavailable",
          "You can only change this in the desktop app."
        ),
      }));
      return;
    }
    await window.electronAPI.relayStop();
    setRelay((p) => ({ ...p, status: { status: "offline" }, error: null }));
  };

  const handleRelayPair = async () => {
    if (!window.electronAPI?.relayPairingCode) {
      setRelay((p) => ({
        ...p,
        error: t(
          "profile.relayUnavailable",
          "You can only change this in the desktop app."
        ),
      }));
      return;
    }
    setRelayBusy(true);
    setRelayCode(null);
    setRelayQrImage(null);
    try {
      const data = await window.electronAPI.relayPairingCode();
      setRelayCode(data?.code ?? null);
      if (data?.code && relay?.url) {
        try {
          const qrImageDataUrl = await QRCode.toDataURL(
            JSON.stringify({ relayUrl: relay.url, code: data.code }),
            { width: 220, margin: 1 }
          );
          setRelayQrImage(qrImageDataUrl);
        } catch (qrError) {
          console.log("Relay QR generation error", qrError);
        }
      }
    } catch (e) {
      setRelay((p) => ({ ...p, error: e?.message }));
    } finally {
      setRelayBusy(false);
    }
  };

  const renderBadge = (icon, labelKey, fallback, color) => (
    <View style={[styles.badge, { borderColor: color }]}>
      <AppIcon icon={icon} size={11} color={color} />
      <AppText style={[styles.badgeLabel, { color }]} preventTranslation>
        {t(labelKey, fallback)}
      </AppText>
    </View>
  );

  const renderStep = (n, text) => (
    <View style={styles.stepRow}>
      <View style={styles.stepNumberWrap}>
        <AppText style={styles.stepNumber} preventTranslation>
          {n}
        </AppText>
      </View>
      <AppText
        wrapperStyle={styles.stepTextWrap}
        style={styles.stepText}
        color={currentTheme?.theme?.darkGrey}
        preventTranslation
      >
        {text}
      </AppText>
    </View>
  );

  const renderCopyRow = (label, value, copyKey) => (
    <View style={styles.remoteValueRow}>
      <AppText style={styles.remoteMono} preventTranslation>
        {label}: {value}
      </AppText>
      <AppText
        style={styles.remoteCopy}
        isClickable
        onPress={() => copyLanValue(value, copyKey)}
        preventTranslation
      >
        {copiedKey === copyKey
          ? t("common.copied", "Copied")
          : t("common.copy", "Copy")}
      </AppText>
    </View>
  );

  return (
    <View style={styles.container}>
      <View style={styles.sectionInner}>
        <AppText
          style={styles.introTitle}
          color={currentTheme?.theme?.font_color}
        >
          {t("profile.peeringTitle", "Control this computer from your phone")}
        </AppText>
        <AppText
          style={styles.introDesc}
          color={currentTheme?.theme?.darkGrey}
          preventTranslation
        >
          {t(
            "profile.peeringIntro",
            "Connect the ToothFairyAI mobile app to this computer so you can drive its AI coder from your phone."
          )}
        </AppText>
        <AppText
          style={styles.introDesc}
          color={currentTheme?.theme?.darkGrey}
          preventTranslation
        >
          {t(
            "profile.peeringChoose",
            "Two ways to connect — pick one, or set up both."
          )}
        </AppText>
      </View>

      <View style={styles.card}>
        {renderBadge(
          AppAssets.icons.network,
          "profile.remoteAccessBadge",
          "Same Wi-Fi only",
          currentTheme?.theme?.primary
        )}
        <AppSwitch
          wrapperStyle={{
            paddingVertical: 8,
            width: "100%",
          }}
          descriptionStyle={{
            marginRight: 10,
          }}
          showTextInputLabel
          label={t(
            "profile.remoteAccess",
            "Let my phone control this computer"
          )}
          isTicked={!!lan?.enabled}
          disabled={lanPending}
          onChange={handleLanToggle}
          description={t(
            "profile.remoteAccessDesc",
            "Your phone can use the AI coder on this computer while both are on the same Wi-Fi network. Nothing travels over the internet."
          )}
          descriptionIcon={false}
          descriptionColor={currentTheme?.theme?.darkGrey}
          pressableStyle={{ marginRight: 20 }}
        />

        {lanReady && (
          <View style={styles.readyWrap}>
            <AppText
              style={styles.cardSubtitle}
              color={currentTheme?.theme?.font_color}
            >
              {t("profile.remoteSetupTitle", "Set up your phone")}
            </AppText>
            {renderStep(
              1,
              t(
                "profile.remoteSetupStep1",
                "Open the ToothFairyAI app on your phone."
              )
            )}
            {renderStep(
              2,
              t(
                "profile.remoteSetupStep2",
                "In the side menu, tap Mobile peering, then Add a desktop."
              )
            )}
            {renderStep(
              3,
              t(
                "profile.remoteSetupStep3",
                "Copy the address and password below into it."
              )
            )}
            {(lan?.urls || []).map((u) => renderCopyRow(
              t("profile.remoteAddressLabel", "Address"),
              u,
              u
            ))}
            {renderCopyRow(
              t("profile.remotePassword", "Password"),
              lan.password,
              "password"
            )}
          </View>
        )}

        {lanWaiting && (
          <View style={styles.stateBox}>
            <AppText
              style={styles.stateBoxTitle}
              color={currentTheme?.theme?.font_color}
            >
              {t("profile.remoteTurningOnTitle", "Turning on…")}
            </AppText>
            <AppText
              style={styles.stateBoxDesc}
              color={currentTheme?.theme?.darkGrey}
              preventTranslation
            >
              {t(
                "profile.remoteTurningOnDesc",
                "This finishes by itself once your workspace has loaded — no action needed."
              )}
            </AppText>
            {isElectron && window.electronAPI?.startTfcode && (
              <ToothFairyButton
                title={t("profile.remoteTurnOnNow", "Turn on now")}
                onPress={handleTurnOnNow}
                disabled={turnOnBusy}
                wrapperStyle={{
                  marginTop: 10,
                  minWidth: 150,
                  height: Constants.appButtonHeight,
                  alignSelf: "flex-start",
                }}
              />
            )}
          </View>
        )}

        {needsRestart && (
          <View style={styles.stateBox}>
            <AppText
              style={styles.stateBoxTitle}
              color={currentTheme?.theme?.font_color}
            >
              {t("profile.remoteRestartTitle", "One restart needed")}
            </AppText>
            <AppText
              style={styles.stateBoxDesc}
              color={currentTheme?.theme?.darkGrey}
              preventTranslation
            >
              {t(
                "profile.remoteRestartDesc",
                "Restart the app once to finish turning this on. Your phone's connection details will appear here afterwards."
              )}
            </AppText>
            <ToothFairyButton
              title={t("profile.remoteRestartNow", "Restart now")}
              onPress={() => window.electronAPI?.relaunchApp?.()}
              wrapperStyle={{
                marginTop: 10,
                minWidth: 150,
                height: Constants.appButtonHeight,
                alignSelf: "flex-start",
              }}
            />
          </View>
        )}
      </View>

      <View style={styles.card}>
        {renderBadge(
          AppAssets.icons.internet,
          "profile.relayBadge",
          "Over the internet",
          TEAL
        )}
        <AppText
          style={styles.cardTitle}
          color={currentTheme?.theme?.font_color}
        >
          {t("profile.relay", "Use my phone from anywhere")}
        </AppText>
        <AppText
          style={styles.cardDesc}
          color={currentTheme?.theme?.darkGrey}
          preventTranslation
        >
          {t(
            "profile.relayDesc",
            "Connects to ToothFairyAI's always-on relay server over the internet, so your phone can reach this computer from anywhere — even when you're away or the lid is closed. Your computer is kept awake while this is on."
          )}
        </AppText>

        <AppText
          style={styles.cardSubtitle}
          color={currentTheme?.theme?.font_color}
        >
          {t("profile.relaySetupTitle", "Set-up")}
        </AppText>
        {renderStep(
          1,
          t(
            "profile.relayStep1",
            "Press Turn on — the status dot turns green when it's working."
          )
        )}
        {renderStep(
          2,
          t(
            "profile.relayStep2",
            "Press Show pairing code to display a QR code."
          )
        )}
        {renderStep(
          3,
          t(
            "profile.relayStep3",
            "Open the ToothFairyAI app on your phone, tap Mobile peering, then scan the QR code (or type the code)."
          )
        )}

        {relay?.url && (
          <View style={styles.relayStatusRow}>
            <AppText
              style={{
                fontSize: currentTheme?.theme?.small_font_size,
                color: currentTheme?.theme?.darkGrey,
                flexShrink: 1,
              }}
            >
              {t("profile.relayRegion", "Relay region (automatic)")}: "
              {relay.url}"
            </AppText>
          </View>
        )}
        <View style={styles.relayStatusRow}>
          <View
            style={[styles.relayStatusDot, { backgroundColor: relayDotColor }]}
          />
          <AppText
            style={{
              fontSize: currentTheme?.theme?.small_font_size,
              color: relay?.error ? CORAL : currentTheme?.theme?.darkGrey,
              flexShrink: 1,
            }}
            preventTranslation
          >
            {t("profile.relayStatus", "Status")}: {relayStateLabel}
          </AppText>
        </View>
        {relay?.url && (
          <View style={styles.relayButtonsRow}>
            <ToothFairyButton
              title={t("profile.relayStart", "Turn on")}
              onPress={handleRelayStart}
              disabled={relayBusy}
              wrapperStyle={{
                marginTop: 10,
                minWidth: 130,
                height: Constants.appButtonHeight,
              }}
            />
            <ToothFairyButton
              title={t("profile.relayShowCode", "Show pairing code")}
              onPress={handleRelayPair}
              disabled={relayBusy}
              fontColor={currentTheme?.theme?.primary}
              backgroundColor={currentTheme?.theme?.transparent}
              wrapperStyle={{
                marginTop: 10,
                minWidth: 170,
                height: Constants.appButtonHeight,
              }}
            />
            <ToothFairyButton
              title={t("profile.relayStop", "Turn off")}
              onPress={handleRelayStop}
              disabled={relayBusy}
              fontColor={CORAL}
              backgroundColor={currentTheme?.theme?.transparent}
              wrapperStyle={{
                marginTop: 10,
                minWidth: 110,
                height: Constants.appButtonHeight,
              }}
            />
          </View>
        )}
        {relayCode && (
          <View style={styles.relayPairWrap}>
            {relayQrImage && (
              <Image
                source={{ uri: relayQrImage }}
                style={styles.relayQrImage}
              />
            )}
            <View style={styles.relayPairTextWrap}>
              <AppText
                style={styles.relayCode}
                color={currentTheme?.theme?.font_color}
                preventTranslation
              >
                {t("profile.relayPairCode", "Pairing code")}: {relayCode}
              </AppText>
              <AppText
                style={styles.relayCodeHint}
                color={currentTheme?.theme?.darkGrey}
                preventTranslation
              >
                {t(
                  "profile.relayPairCodeHint",
                  "Scan this in the mobile app — the code works for 10 minutes."
                )}
              </AppText>
            </View>
          </View>
        )}
      </View>
    </View>
  );
};

const styleGenerator = (currentTheme) => {
  return StyleSheet.create({
    container: {
      flex: 1,
      alignItems: "center",
      paddingTop: 20,
      paddingHorizontal: 20,
    },
    sectionInner: {
      width: "100%",
      maxWidth: 600,
      marginBottom: 10,
    },
    introTitle: {
      width: "100%",
      fontSize: 20,
      fontWeight: "800",
      textAlign: "left",
      marginBottom: 4,
    },
    introDesc: {
      width: "100%",
      fontSize: 14,
      textAlign: "left",
      marginBottom: 4,
    },
    card: {
      width: "100%",
      maxWidth: 600,
      marginBottom: 16,
      padding: 16,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: currentTheme?.theme?.border_color || "#2A2F3E",
      backgroundColor:
        currentTheme?.theme?.editor_background_color || "transparent",
    },
    badge: {
      flexDirection: "row",
      alignItems: "center",
      gap: 5,
      alignSelf: "flex-start",
      borderWidth: 1,
      borderRadius: 999,
      paddingHorizontal: 10,
      paddingVertical: 3,
      marginBottom: 8,
    },
    badgeLabel: {
      fontSize: 12,
      fontWeight: "700",
    },
    cardTitle: {
      width: "100%",
      fontSize: 16,
      fontWeight: "700",
      textAlign: "left",
      marginBottom: 4,
    },
    cardDesc: {
      width: "100%",
      fontSize: 13,
      textAlign: "left",
      marginBottom: 10,
    },
    cardSubtitle: {
      width: "100%",
      fontSize: 14,
      fontWeight: "700",
      textAlign: "left",
      marginTop: 8,
      marginBottom: 4,
    },
    stepRow: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 8,
      marginTop: 6,
      width: "100%",
    },
    stepNumberWrap: {
      width: 20,
      height: 20,
      borderRadius: 10,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: currentTheme?.theme?.transparent
        ? "rgba(29,184,198,0.12)"
        : "rgba(29,184,198,0.12)",
      flexShrink: 0,
    },
    stepNumber: {
      fontSize: 11,
      fontWeight: "800",
      color: currentTheme?.theme?.primary || "#1DB8C6",
    },
    stepText: {
      fontSize: 13,
      flex: 1,
      textAlign: "left",
      lineHeight: 18,
    },
    stepTextWrap: {
      flex: 1,
      alignItems: "flex-start",
    },
    readyWrap: {
      width: "100%",
      marginTop: 8,
    },
    stateBox: {
      width: "100%",
      marginTop: 10,
      borderWidth: 1,
      borderRadius: 10,
      padding: 12,
      borderColor: currentTheme?.theme?.border_color || "#2A2F3E",
    },
    stateBoxTitle: {
      width: "100%",
      fontSize: 14,
      fontWeight: "700",
      textAlign: "left",
    },
    stateBoxDesc: {
      width: "100%",
      fontSize: 13,
      textAlign: "left",
      marginTop: 3,
    },
    remoteValueRow: {
      width: "100%",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      marginTop: 6,
    },
    remoteMono: {
      fontSize: 13,
      flexShrink: 1,
    },
    remoteCopy: {
      fontSize: 13,
      fontWeight: "700",
      flexShrink: 0,
      color: currentTheme?.theme?.primary || "#1DB8C6",
    },
    relayStatusRow: {
      width: "100%",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: 6,
      marginTop: 12,
    },
    relayStatusDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      flexShrink: 0,
    },
    relayButtonsRow: {
      width: "100%",
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: 10,
    },
    relayCode: {
      width: "100%",
      fontSize: 14,
      fontWeight: "700",
      textAlign: "left",
      marginTop: 12,
    },
    relayPairWrap: {
      width: "100%",
      flexDirection: "row",
      alignItems: "center",
      gap: 14,
      marginTop: 12,
    },
    relayQrImage: {
      width: 130,
      height: 130,
      borderRadius: 8,
      flexShrink: 0,
    },
    relayPairTextWrap: {
      flex: 1,
      alignItems: "flex-start",
    },
    relayCodeHint: {
      width: "100%",
      fontSize: 12,
      textAlign: "left",
      marginTop: 4,
      lineHeight: 16,
    },
  });
};

export default MobilePeeringSection;
